import { ChartManager } from './chart.manager';

import { d3renderText, D3TextData } from './chart.d3';

import { clamp, cloneObject, roundTo, unique } from '@23degrees/shared-code/utilities';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { getMoment, objToMoment } from '@23degrees/shared-code/utilities/date';
import chroma from 'chroma-js';
import { isHorizontalType, isPieType, isTimeType, isVerticalType } from '@23degrees/shared-code/config/constants';

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
const color2css = (color: string) => chroma(color).hex();

const fixLineHeight = (obj: unknown): number => {
  if (typeof obj === 'number') return obj;
  return 1.25;
};

export const timeTicks = <T>(values: T[], tickNum = 7) => {
  if (values.length <= tickNum) return values;
  const factor = Math.ceil(values.length / tickNum);
  return values.filter((obj, idx) => idx % factor === (values.length + factor - 1) % factor);
};

export const processLabelAxis = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { labelAxis } = typeSpecific.chart.axis;
  const { grid } = typeSpecific.chart;
  const { titleStyle, valueStyle } = labelAxis;

  const dynamicTitleStyle = cloneObject(titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(titleStyle);

  const dynamicValueStyle = cloneObject(valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  // const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  // const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  const valueMarginLeft = chartManager.getStyleInPx(dynamicValueStyle.margin, 'left');
  const valueMarginRight = chartManager.getStyleInPx(dynamicValueStyle.margin, 'right');

  const { margin, padding } = typeSpecific.chart.style;
  const offsetLeft = chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  if (!labelAxis.enabled || isPieType(type)) {
    if (isVerticalType(type) || isTimeType(type)) chartManager.updateChartHeight(0);
    if (isHorizontalType(type)) chartManager.updateChartWidth(0);
    if (isPieType(type)) {
      chartManager.updateChartWidth(0);
      chartManager.updateChartHeight(0);
    }
    return;
  }

  if (isVerticalType(type) || isTimeType(type)) {
    let axisHeight = 0;
    const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType, format } = typeSpecific.fields[mapField];
    let axisValues: string[] | ReturnType<typeof objToMoment>[] = unique(chartManager.data.map((obj) => obj[mapField] as string).filter((obj) => obj !== null));
    const bandWidth = chartManager.xScaleBandWidth();

    const { sorting } = typeSpecific.chart;
    const isSortedByTime = !sorting.enabled || sorting.sortFields.length === 0 || sorting.sortFields[0] === mapField;

    let isContinuous = true;
    if (dataType === 'date') {
      const { inputFormat } = format.date;
      const momentValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));
      if (momentValues.length > 1) {
        let avgDiff = momentValues[1].diff(momentValues[0]);
        for (let idx = 1; idx < momentValues.length; idx++) {
          const curDiff = momentValues[idx].diff(momentValues[idx - 1]);
          if (momentValues[idx].isBefore(momentValues[idx - 1])) isContinuous = false;
          if (curDiff < avgDiff * 0.5 || curDiff > avgDiff * 1.5) isContinuous = false;
          avgDiff = (avgDiff + curDiff) / 2;
        }
      }
    }

    const isTimeValueAndType = dataType === 'date' && ((isSortedByTime && isContinuous) || isTimeType(type)); // this takes render pipeline area/line sorting into account
    if (isTimeValueAndType) {
      const { inputFormat } = format.date;
      const { dateFormat } = labelAxis.format.date;

      if (isTimeType(type) || oneLineHeight * 1.5 >= bandWidth) {
        axisValues = timeTicks(axisValues, labelAxis.tickInput ?? labelAxis.tickCount);
      }

      labelAxis.tickCount = axisValues.length;
      axisValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));

      if (Array.isArray(labelAxis.tickValues)) {
        const userValues = [];
        for (const value of labelAxis.tickValues) {
          const obj = objToMoment(value, dateFormat, format.locale, false);
          if (getMoment().isMoment(obj) && obj.isValid()) userValues.push(obj);
        }

        if (userValues.length > 0) {
          axisValues = userValues;
        }
      }
    }

    // labelAxis.format.formatType = dataType;

    chartManager.labelOffsetArray = [];
    chartManager.labelRotationDegrees = 0;
    const availableWidth = chartManager.xScale.range()[1] / axisValues.length;
    // if (isTimeValueAndType) availableWidth *= 3 / 4;

    if (labelAxis.placementMode === 'rotate') {
      // process values
      const processedValues = [];
      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        let splitLines = labelAxis.maxLines;
        if (labelAxis.autoLines) {
          const estLines = Math.floor(textWidth / oneLineHeight / labelAxis.lineBreakFactor);
          const divLines = Math.ceil(textWidth / availableWidth);
          const maxLines = Math.min(divLines, Math.floor(availableWidth / oneLineHeight));
          splitLines = clamp(estLines, 1, maxLines);
        }

        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);
        const lineHeight = (textSplit.lines.length - 1) * dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
        const textHeight = textSplit.lines.length * dynamicValueStyle.size + lineHeight;

        processedValues.push({
          ignore: false,
          splitLines,
          textSplit,
          textWidth: textSplit.maxWidth,
          textHeight,
        });
      }

      // calculate rotation
      if (labelAxis.autoRotation || labelAxis.minRotation < 0) {
        let rotationRequired = false;
        for (const obj of processedValues) {
          const objIdx = processedValues.indexOf(obj);

          let targetWidth = availableWidth;
          if (isTimeValueAndType && objIdx === 0) targetWidth = availableWidth * 0.67;
          if (isTimeValueAndType && objIdx === processedValues.length - 1) targetWidth = availableWidth * 0.67;

          if (obj.textWidth > targetWidth) {
            rotationRequired = true;
            break;
          }
        }

        if (rotationRequired) {
          for (const obj of processedValues) {
            if (obj.textHeight > availableWidth) {
              obj.ignore = true;
              chartManager.labelRotationDegrees = 89;
            } else {
              // check wether its the first/last value in the array
              let estimatedWidth = obj.textWidth;
              let estimatedRotation = 0;

              if (processedValues.indexOf(obj) === 0) {
                const firstWidth = chartManager.yAxisWidth + availableWidth / 2; // offsetLeft
                estimatedRotation = (Math.acos(Math.min(firstWidth, obj.textWidth) / obj.textWidth) * 180) / Math.PI;

                for (let idx = 0; idx < 5; idx++) {
                  estimatedWidth = obj.textWidth + Math.sin((estimatedRotation / 180) * Math.PI) * obj.textHeight;
                  estimatedRotation = (Math.acos(Math.min(firstWidth, estimatedWidth) / estimatedWidth) * 180) / Math.PI;
                }
              } else {
                let targetWidth = availableWidth;
                if (processedValues.indexOf(obj) === processedValues.length - 1) targetWidth = availableWidth * 0.67;

                estimatedRotation = (Math.asin(obj.textHeight / targetWidth) * 180) / Math.PI;

                for (let idx = 0; idx < 5; idx++) {
                  estimatedWidth = obj.textWidth + Math.sin((estimatedRotation / 180) * Math.PI) * obj.textHeight;
                  estimatedRotation = (Math.asin(obj.textHeight / targetWidth) * 180) / Math.PI;
                }
              }

              chartManager.labelRotationDegrees = clamp(Math.max(chartManager.labelRotationDegrees, estimatedRotation), 22.5, 89); // 11.25, 22.5
            }
          }
        }
      } else {
        chartManager.labelRotationDegrees = labelAxis.minRotation;
      }

      // calculate height of final rotation
      for (const obj of processedValues) {
        if (obj.ignore) continue;

        const rotatedWidth = Math.sin((chartManager.labelRotationDegrees / 180) * Math.PI) * obj.textWidth;
        const rotatedHeight = Math.cos((chartManager.labelRotationDegrees / 180) * Math.PI) * obj.textHeight;
        const labelOverflow = Math.sin((chartManager.labelRotationDegrees / 180) * Math.PI) * (dynamicValueStyle.size / labelAxis.lineBreakFactor);
        const rotatedLabelSize = rotatedWidth + rotatedHeight - labelOverflow;

        axisHeight = Math.max(axisHeight, rotatedLabelSize);
      }
    }

    if (labelAxis.placementMode === 'offset') {
      for (let idx = 1; idx <= Math.min(7, axisValues.length); idx++) {
        let foundOffset = true;
        const offsetWidth = availableWidth * idx;

        chartManager.labelOffsetArray.length = idx;
        chartManager.labelOffsetArray.fill(0);

        let axisIdx = 0;
        for (const obj of axisValues) {
          const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
          const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

          let splitLines = labelAxis.maxLines;
          if (labelAxis.autoLines) {
            const estLines = Math.floor(textWidth / oneLineHeight / labelAxis.lineBreakFactor);
            const divLines = Math.ceil(textWidth / availableWidth);
            const maxLines = Math.min(divLines, Math.floor(availableWidth / oneLineHeight));
            splitLines = clamp(estLines, 1, maxLines);
          }

          const textSplit = chartManager.splitTextToWidth(niceObj, offsetWidth, dynamicValueStyle);
          if (textSplit.maxWidth > offsetWidth || textSplit.lines.length > splitLines) {
            foundOffset = false;
            // axisIdx++;
            // break;
          }

          chartManager.labelOffsetArray[axisIdx % idx] = Math.max(chartManager.labelOffsetArray[axisIdx % idx], textSplit.lines.length);
          axisIdx++;
        }

        if (foundOffset) {
          break;
        }
      }

      let lineCount = 0;
      for (const offset of chartManager.labelOffsetArray) {
        lineCount += offset;
      }

      const lineHeight = (lineCount - 1) * dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
      const textHeight = lineCount * dynamicValueStyle.size + lineHeight;
      axisHeight = Math.max(axisHeight, textHeight);
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisHeight += totalHeight;
    }

    // chartManager.updateChartWidth(0);
    // console.log('[labelAxis]', 'height', axisHeight);
    chartManager.updateChartHeight(axisHeight);
  }

  if (isHorizontalType(type)) {
    let axisWidth = 0;
    const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
    const axisValues = unique(chartManager.data.map((obj) => obj[mapField] as string).filter((obj) => obj !== null));
    const availableHeight = chartManager.yScale.range()[1] / axisValues.length;

    if (!typeSpecific.chart.axis.labelAxis.labelsAboveBars) {
      if (availableHeight >= oneLineHeight) {
        for (const obj of axisValues) {
          const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
          const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

          let splitLines = labelAxis.maxLines;
          if (labelAxis.autoLines) {
            const estLines = Math.floor(textWidth / oneLineHeight / labelAxis.lineBreakFactor);
            const maxLines = Math.floor(availableHeight / oneLineHeight);
            splitLines = clamp(estLines, 1, maxLines);
          }

          const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);
          axisWidth = Math.max(axisWidth, Math.ceil(textSplit.maxWidth));
        }
      }

      chartManager.labelTextMaxWidth = axisWidth;
      chartManager.labelAvailableHeight = availableHeight;
    } else {
      let maxWidth = 0;
      const availableWidth = chartManager.xScale(chartManager.xScale.domain()[1]) - chartManager.xScale(0) - (valueMarginLeft + valueMarginRight);

      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        const textSplit = chartManager.splitTextToWidth(niceObj, availableWidth, dynamicValueStyle);
        maxWidth = Math.max(maxWidth, Math.ceil(textSplit.maxWidth));
      }

      chartManager.labelTextMaxWidth = maxWidth;
      chartManager.labelAvailableHeight = availableHeight;
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisWidth += totalHeight;
    }

    axisWidth += 1; // prevent font from sticking to chart
    chartManager.updateChartWidth(axisWidth);
  }
};

export const processValueAxis = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { grid, gridUser, gridZero } = typeSpecific.chart;
  const { valueAxis } = typeSpecific.chart.axis;
  const { titleStyle, valueStyle } = valueAxis;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);

  const dynamicTitleStyle = cloneObject(titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(titleStyle);

  const dynamicValueStyle = cloneObject(valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  if (!valueAxis.enabled || isPieType(type)) {
    if (isVerticalType(type) || isTimeType(type)) chartManager.updateChartWidth(0);
    if (isHorizontalType(type)) chartManager.updateChartHeight(0);
    if (isPieType(type)) {
      chartManager.updateChartWidth(0);
      chartManager.updateChartHeight(0);
    }
    return;
  }

  if (isVerticalType(type) || isTimeType(type)) {
    let axisWidth = 0;
    const axisValues = [];
    if (grid.enabled) axisValues.push(...chartManager.yScale.ticks(Number.parseInt(String(grid.yAxis), 10)));
    if (gridUser.enabled) axisValues.push(...gridUser.values);
    if (gridZero.enabled && chartManager.rangeMin <= 0) axisValues.push(0);

    const availableHeight = chartManager.yScale.range()[1] / axisValues.length;

    if (availableHeight >= oneLineHeight) {
      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, valueAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        let splitLines = valueAxis.maxLines;
        if (valueAxis.autoLines) {
          const estLines = Math.floor(textWidth / oneLineHeight / valueAxis.lineBreakFactor);
          const maxLines = Math.floor(availableHeight / oneLineHeight);
          splitLines = clamp(estLines, 1, maxLines);
        }

        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);
        axisWidth = Math.max(axisWidth, Math.ceil(textSplit.maxWidth));
      }
    }

    chartManager.valueTextMaxWidth = axisWidth;

    if (valueAxis.title && valueAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(valueAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisWidth += totalHeight;
    }

    chartManager.updateChartWidth(axisWidth);
  }

  if (isHorizontalType(type)) {
    let axisHeight = 0;
    const axisValues = [];
    if (grid.enabled) axisValues.push(...chartManager.xScale.ticks(Number.parseInt(String(grid.xAxis), 10)));
    if (gridUser.enabled) axisValues.push(...gridUser.values);
    if (gridZero.enabled && chartManager.rangeMin <= 0) axisValues.push(0);

    chartManager.valueOffsetArray = [];
    chartManager.valueRotationDegrees = 0;
    const availableWidth = chartManager.xScale.range()[1] / axisValues.length;

    if (valueAxis.placementMode === 'rotate') {
      // process values
      const processedValues = [];
      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, valueAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        let splitLines = valueAxis.maxLines;
        if (valueAxis.autoLines) {
          const estLines = Math.floor(textWidth / oneLineHeight / valueAxis.lineBreakFactor);
          const divLines = Math.ceil(textWidth / availableWidth);
          const maxLines = Math.min(divLines, Math.floor(availableWidth / oneLineHeight));
          splitLines = clamp(estLines, 1, maxLines);
        }

        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);
        const lineHeight = (textSplit.lines.length - 1) * dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
        const textHeight = textSplit.lines.length * dynamicValueStyle.size + lineHeight;

        processedValues.push({
          ignore: false,
          splitLines,
          textSplit,
          textWidth: textSplit.maxWidth,
          textHeight,
        });
      }

      // calculate rotation
      if (valueAxis.autoRotation || valueAxis.minRotation < 0) {
        let rotationRequired = false;
        for (const obj of processedValues) {
          const objIdx = processedValues.indexOf(obj);

          let targetWidth = availableWidth;
          if (objIdx === 0) targetWidth = availableWidth * 0.67;
          if (objIdx === processedValues.length - 1) targetWidth = availableWidth * 0.67;

          if (obj.textWidth > targetWidth) {
            rotationRequired = true;
            break;
          }
        }

        if (rotationRequired) {
          for (const obj of processedValues) {
            if (obj.textHeight > availableWidth) {
              obj.ignore = true;
              // chartManager.valueRotationDegrees = 89;
            } else {
              // check wether its the first/last value in the array
              let estimatedWidth = obj.textWidth;
              let estimatedRotation = 0;

              if (processedValues.indexOf(obj) === 0) {
                const firstWidth = chartManager.yAxisWidth + availableWidth / 2; // offsetLeft
                estimatedRotation = (Math.acos(Math.min(firstWidth, obj.textWidth) / obj.textWidth) * 180) / Math.PI;

                for (let idx = 0; idx < 5; idx++) {
                  estimatedWidth = obj.textWidth + Math.sin((estimatedRotation / 180) * Math.PI) * obj.textHeight;
                  estimatedRotation = (Math.acos(Math.min(firstWidth, estimatedWidth) / estimatedWidth) * 180) / Math.PI;
                }
              } else {
                let targetWidth = availableWidth;
                if (processedValues.indexOf(obj) === processedValues.length - 1) targetWidth = availableWidth * 0.67;

                estimatedRotation = (Math.asin(obj.textHeight / targetWidth) * 180) / Math.PI;

                for (let idx = 0; idx < 5; idx++) {
                  estimatedWidth = obj.textWidth + Math.sin((estimatedRotation / 180) * Math.PI) * obj.textHeight;
                  estimatedRotation = (Math.asin(obj.textHeight / targetWidth) * 180) / Math.PI;
                }
              }

              chartManager.valueRotationDegrees = clamp(Math.max(chartManager.valueRotationDegrees, estimatedRotation), 22.5, 89);
            }
          }
        }
      } else {
        chartManager.valueRotationDegrees = Math.max(0, valueAxis.minRotation);
      }

      // calculate height of final rotation
      for (const obj of processedValues) {
        if (obj.ignore) continue;

        const rotatedWidth = Math.sin((chartManager.valueRotationDegrees / 180) * Math.PI) * obj.textWidth;
        const rotatedHeight = Math.cos((chartManager.valueRotationDegrees / 180) * Math.PI) * obj.textHeight;
        const labelOverflow = Math.sin((chartManager.valueRotationDegrees / 180) * Math.PI) * (dynamicValueStyle.size / valueAxis.lineBreakFactor);
        const rotatedLabelSize = rotatedWidth + rotatedHeight - labelOverflow;

        axisHeight = Math.max(axisHeight, rotatedLabelSize);
      }
    }

    if (valueAxis.placementMode === 'offset') {
      for (let idx = 1; idx <= Math.min(7, axisValues.length); idx++) {
        let foundOffset = true;
        const offsetWidth = availableWidth * idx;

        chartManager.valueOffsetArray.length = idx;
        chartManager.valueOffsetArray.fill(0);

        let axisIdx = 0;
        for (const obj of axisValues) {
          const niceObj = chartManager.formatPipe.transform(obj, valueAxis.format);
          const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

          let splitLines = valueAxis.maxLines;
          if (valueAxis.autoLines) {
            const estLines = Math.floor(textWidth / oneLineHeight / valueAxis.lineBreakFactor);
            const divLines = Math.ceil(textWidth / availableWidth);
            const maxLines = Math.min(divLines, Math.floor(availableWidth / oneLineHeight));
            splitLines = clamp(estLines, 1, maxLines);
          }

          const textSplit = chartManager.splitTextToWidth(niceObj, offsetWidth, dynamicValueStyle);
          if (textSplit.maxWidth > offsetWidth || textSplit.lines.length > splitLines) {
            foundOffset = false;
            // axisIdx++;
            // break;
          }

          chartManager.valueOffsetArray[axisIdx % idx] = Math.max(chartManager.valueOffsetArray[axisIdx % idx], textSplit.lines.length);
          axisIdx++;
        }

        if (foundOffset) {
          break;
        }
      }

      let lineCount = 0;
      for (const offset of chartManager.valueOffsetArray) {
        lineCount += offset;
      }

      const lineHeight = (lineCount - 1) * dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
      const textHeight = lineCount * dynamicValueStyle.size + lineHeight;
      axisHeight = Math.max(axisHeight, textHeight);
    }

    if (valueAxis.title && valueAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(valueAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisHeight += totalHeight;
    }

    // console.log('[valueAxis]', 'height', axisHeight);
    chartManager.updateChartHeight(axisHeight);
  }
};

export const renderLabelAxis = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { grid, values } = typeSpecific.chart;
  const { labelAxis } = typeSpecific.chart.axis;
  const { backgroundColor } = typeSpecific.style.background;
  const textColor = chroma(backgroundColor).hsl()[2] > values.lightnessThreshold ? values.darkColor : values.lightColor;

  const { margin, padding } = typeSpecific.chart.style;
  const marginLeft = chartManager.getStyleInPx(margin, 'left');
  const marginTop = chartManager.getStyleInPx(margin, 'top');
  const marginBottom = chartManager.getStyleInPx(margin, 'bottom');
  const paddingBottom = chartManager.getStyleInPx(padding, 'bottom');
  const offsetTop = chartManager.getOffset('top') + chartManager.dataLabelOffset.bottom; // chartManager.getStyleInPx(margin, 'top') + chartManager.getStyleInPx(padding, 'top');
  const offsetLeft = chartManager.getOffset('left') + chartManager.dataLabelOffset.left; // chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  const dynamicTitleStyle = cloneObject(labelAxis.titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(labelAxis.titleStyle);

  const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  const valueMarginLeft = chartManager.getStyleInPx(dynamicValueStyle.margin, 'left');
  const valueMarginRight = chartManager.getStyleInPx(dynamicValueStyle.margin, 'right');

  const data: D3TextData[] = [];
  if (!labelAxis.enabled) {
    d3renderText(chartManager.chart.element, data, '.labelAxis');
    return;
  }

  if (isVerticalType(type) || isTimeType(type)) {
    const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType, format } = typeSpecific.fields[mapField];
    let axisValues: string[] | ReturnType<typeof objToMoment>[] = unique(chartManager.data.map((obj) => obj[mapField] as string).filter((obj) => obj !== null));
    const bandWidth = chartManager.xScaleBandWidth();

    const { sorting } = typeSpecific.chart;
    const isSortedByTime = !sorting.enabled || sorting.sortFields.length === 0 || sorting.sortFields[0] === mapField;

    let isContinuous = true;
    if (dataType === 'date') {
      const { inputFormat } = format.date;
      const momentValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));
      if (momentValues.length > 1) {
        let avgDiff = momentValues[1].diff(momentValues[0]);
        for (let idx = 1; idx < momentValues.length; idx++) {
          const curDiff = momentValues[idx].diff(momentValues[idx - 1]);
          if (momentValues[idx].isBefore(momentValues[idx - 1])) isContinuous = false;
          if (curDiff < avgDiff * 0.5 || curDiff > avgDiff * 1.5) isContinuous = false;
          avgDiff = (avgDiff + curDiff) / 2;
        }
      }
    }

    const isTimeValueAndType = dataType === 'date' && ((isSortedByTime && isContinuous) || isTimeType(type)); // this takes render pipeline area/line sorting into account
    if (isTimeValueAndType) {
      const { inputFormat } = format.date;
      const { dateFormat } = labelAxis.format.date;

      if (isTimeType(type) || oneLineHeight * 1.5 >= bandWidth) {
        axisValues = timeTicks(axisValues, labelAxis.tickInput ?? labelAxis.tickCount);
      }

      axisValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));

      if (Array.isArray(labelAxis.tickValues)) {
        const userValues = [];
        for (const value of labelAxis.tickValues) {
          const obj = objToMoment(value, dateFormat, format.locale, false);
          if (getMoment().isMoment(obj) && obj.isValid()) userValues.push(obj);
        }

        if (userValues.length > 0) {
          axisValues = userValues;
        }
      }
    }

    // labelAxis.format.formatType = dataType;

    const availableWidth = chartManager.xScale.range()[1] / axisValues.length;

    let axisIdx = 0;
    for (const obj of axisValues) {
      const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
      const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

      if (labelAxis.placementMode === 'rotate') {
        let splitLines = labelAxis.maxLines;
        if (labelAxis.autoLines) {
          const estLines = Math.floor(textWidth / oneLineHeight / labelAxis.lineBreakFactor);
          const divLines = Math.ceil(textWidth / availableWidth);
          const maxLines = Math.min(divLines, Math.floor(availableWidth / oneLineHeight));
          splitLines = clamp(estLines, 1, maxLines);
        }

        // let textRotation = 0; // labelAxis.minRotation;
        // const textSplit = chartManager.splitTextToWidth(niceObj, splitWidth, dynamicValueStyle);
        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);
        const lineHeight = (textSplit.lines.length - 1) * dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
        const textHeight = textSplit.lines.length * dynamicValueStyle.size + lineHeight;

        if (textHeight > availableWidth) continue;

        const relativeX =
          (isTimeValueAndType ? chartManager.xScaleTime((obj as ReturnType<typeof objToMoment>).toDate()) : chartManager.xScale(obj)) + bandWidth / 2; // (isTimeValueAndType ? 0 : bandWidth / 2);
        const x = chartManager.yAxisWidth + offsetLeft + relativeX;
        const y = chartManager.chartHeight + offsetTop + paddingBottom;

        const center = roundTo((textSplit.lines.length / 2) * dynamicValueStyle.size);
        const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

        let idx = 0;
        for (const line of textSplit.lines) {
          if (chartManager.labelRotationDegrees > 0) {
            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + 0.75}em`;
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x - center),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor: 'end',
              transform: `rotate(-${chartManager.labelRotationDegrees} ${roundTo(x - center)} ${roundTo(y)})`,
            });
          } else {
            let anchor = 'middle';
            if (isTimeValueAndType) {
              // if (relativeX < textSplit.maxWidth / 2) anchor = 'start';
              if (relativeX > chartManager.xScale.range()[1] - textSplit.maxWidth / 2) anchor = 'end';
              // if ((anchor === 'start' || anchor === 'end') && textSplit.maxWidth > availableWidth / 2) continue;
            }

            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + 0.75}em`; // 0.75 is special because rotate..
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor,
            });
          }

          idx++;
        }
      }

      if (labelAxis.placementMode === 'offset') {
        const offsetIdx = axisIdx % chartManager.labelOffsetArray.length;
        const offsetWidth = availableWidth * chartManager.labelOffsetArray.length;

        let offsetHeight = 0;
        for (let idxA = 0; idxA < offsetIdx; idxA++) {
          offsetHeight += chartManager.labelOffsetArray[idxA];
        }

        const textSplit = chartManager.splitTextToWidth(niceObj, offsetWidth, dynamicValueStyle);

        const relativeX =
          (isTimeValueAndType ? chartManager.xScaleTime((obj as ReturnType<typeof objToMoment>).toDate()) : chartManager.xScale(obj)) + bandWidth / 2; // (isTimeValueAndType ? 0 : bandWidth / 2);
        const x = chartManager.yAxisWidth + offsetLeft + relativeX;
        const y = chartManager.chartHeight + offsetTop + paddingBottom;

        let idx = 0;
        for (const line of textSplit.lines) {
          let anchor = 'middle';
          if (isTimeValueAndType) {
            // if (relativeX < textSplit.maxWidth / 2) anchor = 'start';
            if (relativeX > chartManager.xScale.range()[1] - textSplit.maxWidth / 2) anchor = 'end';
            // if ((anchor === 'start' || anchor === 'end') && textSplit.maxWidth > availableWidth / 2) continue;
          }

          const dyOffset = offsetHeight * fixLineHeight(dynamicValueStyle.lineheight.value);
          const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset + 0.75}em`;
          const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

          data.push({
            text: line,
            color: color2css(dynamicValueStyle.color),
            fontSize: roundTo(dynamicValueStyle.size),
            fontStyle,
            fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
            fontFamily: dynamicValueStyle.fontFamily,
            x: roundTo(x),
            y: roundTo(y),
            dx: 0,
            dy,
            anchor,
          });

          idx++;
        }

        axisIdx++;
      }
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);
      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = textSplit.lines.length - 1;
      for (const line of textSplit.lines) {
        const dy = `${idx * -fixLineHeight(dynamicTitleStyle.lineheight.value) - 0.25}em`;

        data.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(offsetLeft + chartManager.yAxisWidth + chartManager.chartWidth / 2),
          y: roundTo(chartManager.chart.height - marginBottom - titleMarginBottom),
          dy,
          anchor: 'middle',
        });

        idx--;
      }
    }
  }

  if (isHorizontalType(type)) {
    const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
    const { dataType, format } = typeSpecific.fields[mapField];
    const axisValues = unique(chartManager.data.map((obj) => obj[mapField] as string).filter((obj) => obj !== null));

    const { sorting } = typeSpecific.chart;
    const isSortedByTime = !sorting.enabled || sorting.sortFields.length === 0 || sorting.sortFields[0] === mapField;

    let isContinuous = true;
    if (dataType === 'date') {
      const { inputFormat } = format.date;
      const momentValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));
      if (momentValues.length > 2) {
        let avgDiff = momentValues[1].diff(momentValues[0]);
        for (let idx = 1; idx < momentValues.length; idx++) {
          const curDiff = momentValues[idx].diff(momentValues[idx - 1]);
          if (curDiff < avgDiff * 0.9 || curDiff > avgDiff * 1.1) isContinuous = false;
          avgDiff = (avgDiff + curDiff) / 2;
        }
      }
    }

    const isTimeValueAndType = dataType === 'date' && ((isSortedByTime && isContinuous) || isTimeType(type)); // this takes render pipeline area/line sorting into account
    if (isTimeValueAndType) {
      // const numTicks = Number.parseInt(String(grid.yAxis), 10);
      // if (axisValues.length > numTicks * 2) {
      //   axisValues = chartManager.yScale.ticks(numTicks);
      //   // axisValues = [chartManager.yDomain[0], ...axisValues, chartManager.yDomain[1]];
      // }
      // axisValues = timeTicks(axisValues, labelAxis.tickInput ?? labelAxis.tickCount);
      // const { inputFormat } = format.date;
      // axisValues = axisValues.map((obj) => objToMoment(obj, inputFormat, format.locale));
    }

    // labelAxis.format.formatType = dataType;

    const availableHeight = chartManager.labelAvailableHeight; // chartManager.yScale.range()[1] / axisValues.length;
    if (availableHeight >= oneLineHeight) {
      let anchor = 'end';
      let x = marginLeft + chartManager.yAxisWidth - 1;
      if (dynamicValueStyle.position === 'auto') {
        anchor = 'middle';
        x -= chartManager.labelTextMaxWidth / 2;
      }

      if (dynamicValueStyle.position === 'left') {
        anchor = 'start';
        x -= chartManager.labelTextMaxWidth;
      }

      if (typeSpecific.chart.axis.labelAxis.labelsAboveBars) {
        x = offsetLeft + chartManager.yAxisWidth + chartManager.xScale(0) + valueMarginLeft;
        anchor = 'start';
      }

      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, labelAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        if (!typeSpecific.chart.axis.labelAxis.labelsAboveBars) {
          let splitLines = labelAxis.maxLines;
          if (labelAxis.autoLines) {
            const estLines = Math.floor(textWidth / oneLineHeight / labelAxis.lineBreakFactor);
            const maxLines = Math.floor(availableHeight / oneLineHeight);
            splitLines = clamp(estLines, 1, maxLines);
          }

          const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);

          const dyOffset = 0.33;
          const y = offsetTop + chartManager.yScale(obj) + chartManager.yScale.bandwidth() / 2;
          const center = roundTo((textSplit.lines.length - 1) / 2);
          const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

          let idx = 0;
          for (const line of textSplit.lines) {
            const dy = `${-center + idx * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset}em`; // 0.75 is special because rotate..

            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor,
            });

            idx++;
          }
        } else {
          const y = offsetTop + chartManager.yScale(obj) + valueMarginTop;
          const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

          const dyOffset = 0.25;
          // const dy = 0.5 * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset + 'em';

          const availableWidth = chartManager.xScale(chartManager.xScale.domain()[1]) - chartManager.xScale(0) - (valueMarginLeft + valueMarginRight);

          let textSplit;
          if (labelAxis.autoLines) {
            textSplit = chartManager.splitTextToWidth(niceObj, availableWidth, dynamicValueStyle);
          } else {
            textSplit = chartManager.splitTextToLines(niceObj, labelAxis.maxLines, dynamicValueStyle);
          }

          const center = 0.5 * fixLineHeight(dynamicValueStyle.lineheight.value); // roundTo((textSplit.lines.length - 1) / 2);

          let idx = 0;
          for (const line of textSplit.lines) {
            const dy = `${center + idx * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset}em`;

            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor,
            });

            idx++;
          }

          // data.push({
          //   text: niceObj,
          //   color: color2css(dynamicValueStyle.color),
          //   fontSize: roundTo(dynamicValueStyle.size),
          //   fontStyle: fontStyle,
          //   fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
          //   fontFamily: dynamicValueStyle.fontFamily,
          //   x: roundTo(x),
          //   y: roundTo(y),
          //   dx: 0,
          //   dy: dy,
          //   anchor: anchor,
          // });
        }
      }
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const x = marginLeft + titleMarginTop;
      const y = marginTop + chartManager.chartHeight / 2;

      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = 0;
      for (const line of textSplit.lines) {
        const dy = `${idx * fixLineHeight(dynamicTitleStyle.lineheight.value) + 0.75}em`;
        data.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(x),
          y: roundTo(y),
          dy,
          anchor: 'middle',
          transform: `rotate(-90 ${roundTo(x)} ${roundTo(y)})`,
        });

        idx++;
      }
    }
  }

  d3renderText(chartManager.chart.element, data, '.labelAxis');
};

export const renderValueAxis = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { grid, gridUser, gridZero, values, bars, dynamicHeight } = typeSpecific.chart;
  const { valueAxis } = typeSpecific.chart.axis;
  const { fieldName } = typeSpecific.dimensions.find((obj) => obj.dimName === 'yAxis');
  const { dataType } = typeSpecific.fields[fieldName];
  const { backgroundColor } = typeSpecific.style.background;
  const textColor = chroma(backgroundColor).hsl()[2] > values.lightnessThreshold ? values.darkColor : values.lightColor;
  // valueAxis.format.formatType = dataType;

  const { margin, padding } = typeSpecific.chart.style;
  const marginLeft = chartManager.getStyleInPx(margin, 'left');
  const marginTop = chartManager.getStyleInPx(margin, 'top');
  const marginBottom = chartManager.getStyleInPx(margin, 'bottom');
  const paddingBottom = chartManager.getStyleInPx(padding, 'bottom');
  const offsetTop = chartManager.getOffset('top'); // chartManager.getStyleInPx(margin, 'top') + chartManager.getStyleInPx(padding, 'top');
  const offsetBottom = chartManager.getOffset('bottom'); // chartManager.getStyleInPx(margin, 'bottom') + chartManager.getStyleInPx(padding, 'bottom');
  const offsetLeft = chartManager.getOffset('left'); // chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  const dynamicTitleStyle = cloneObject(valueAxis.titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(valueAxis.titleStyle);

  const dynamicValueStyle = cloneObject(valueAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(valueAxis.valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  const data: D3TextData[] = [];
  if (!valueAxis.enabled || isPieType(type)) {
    d3renderText(chartManager.chart.element, data, '.valueAxis');
    return;
  }

  if (isVerticalType(type) || isTimeType(type)) {
    let axisValues = [];
    if (grid.enabled) axisValues.push(...chartManager.yScale.ticks(Number.parseInt(String(grid.yAxis), 10)));
    if (gridUser.enabled) axisValues.push(...gridUser.values);
    if (gridZero.enabled) axisValues.push(0);
    axisValues = unique(axisValues.sort((a, b) => a - b));

    const availableHeight = chartManager.yScale.range()[1] / axisValues.length;
    if (availableHeight >= oneLineHeight) {
      let anchor = 'end';
      let x = marginLeft + chartManager.yAxisWidth - 1;
      if (dynamicValueStyle.position === 'auto') {
        anchor = 'middle';
        x -= chartManager.valueTextMaxWidth / 2;
      }

      if (dynamicValueStyle.position === 'left') {
        anchor = 'start';
        x -= chartManager.valueTextMaxWidth;
      }

      let valueIdx = 0;
      for (const obj of axisValues) {
        const niceObj = chartManager.formatPipe.transform(obj, valueAxis.format);
        const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

        let splitLines = valueAxis.maxLines;
        if (valueAxis.autoLines) {
          const estLines = Math.floor(textWidth / dynamicValueStyle.size / valueAxis.lineBreakFactor);
          const maxLines = Math.floor(availableHeight / dynamicValueStyle.size);
          splitLines = clamp(estLines, 1, maxLines);
        }

        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);

        const y = offsetTop + chartManager.yScale(obj);
        const center = roundTo((textSplit.lines.length - 1) / 2);

        let dyOffset = 0.33;
        if (valueIdx === 0) dyOffset -= 0.33 + center;
        if (valueIdx === axisValues.length - 1) dyOffset += 0.33 + center;
        const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

        let idx = 0;
        for (const line of textSplit.lines) {
          // chartManager.valueRotationDegrees > 0
          if (false) {
            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + 0.75}em`;
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x - center),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor: 'end',
              transform: `rotate(-${chartManager.valueRotationDegrees} ${roundTo(x - center)} ${roundTo(y)})`,
            });
          } else {
            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset}em`; // 0.75 is special because rotate..
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor,
            });
          }

          idx++;
        }

        valueIdx++;
      }
    }

    if (valueAxis.title && valueAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(valueAxis.title, maxWidth, dynamicTitleStyle);

      const x = marginLeft + titleMarginTop;
      const y = marginTop + chartManager.chartHeight / 2;

      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = 0;
      for (const line of textSplit.lines) {
        const dy = `${idx * fixLineHeight(dynamicTitleStyle.lineheight.value) + 0.75}em`;
        data.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(x),
          y: roundTo(y),
          dy,
          anchor: 'middle',
          transform: `rotate(-90 ${roundTo(x)} ${roundTo(y)})`,
        });

        idx++;
      }
    }

    d3renderText(chartManager.chart.element, data, '.valueAxis');
  }

  if (isHorizontalType(type)) {
    let axisValues = [];
    if (grid.enabled) axisValues.push(...chartManager.xScale.ticks(Number.parseInt(String(grid.xAxis), 10)));
    if (gridUser.enabled) axisValues.push(...gridUser.values);
    if (gridZero.enabled) axisValues.push(0);
    axisValues = unique(axisValues.sort((a, b) => a - b));

    const bandWidth = chartManager.xScaleBandWidth();
    const availableWidth = chartManager.xScale.range()[1] / axisValues.length;

    let axisIdx = 0;
    for (const obj of axisValues) {
      const niceObj = chartManager.formatPipe.transform(obj, valueAxis.format);
      const textWidth = chartManager.getTextMetrics(niceObj, dynamicValueStyle).width;

      if (valueAxis.placementMode === 'rotate') {
        let splitLines = valueAxis.maxLines;
        if (valueAxis.autoLines) {
          const estLines = Math.floor(textWidth / dynamicValueStyle.size / chartManager.lineBreakFactor);
          const divLines = Math.ceil(textWidth / availableWidth);
          const maxLines = Math.min(divLines, Math.floor(availableWidth / dynamicValueStyle.size));
          splitLines = clamp(estLines, 1, maxLines);
        }

        const textSplit = chartManager.splitTextToLines(niceObj, splitLines, dynamicValueStyle);

        const relativeX = chartManager.xScale(obj) + bandWidth / 2;
        const x = chartManager.yAxisWidth + offsetLeft + relativeX;
        const y = chartManager.totalHeight - chartManager.xAxisHeight - marginBottom;

        const center = roundTo((textSplit.lines.length / 2) * dynamicValueStyle.size);
        const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

        let idx = 0;
        for (const line of textSplit.lines) {
          if (chartManager.valueRotationDegrees > 0) {
            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + 0.75}em`;
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x - center),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor: 'end',
              transform: `rotate(-${chartManager.valueRotationDegrees} ${roundTo(x - center)} ${roundTo(y)})`,
            });
          } else {
            let anchor = 'middle';
            if (true) {
              if (relativeX < textSplit.maxWidth / 2) anchor = 'start';
              if (relativeX > chartManager.xScale.range()[1] - textSplit.maxWidth / 2) anchor = 'end';
              // if ((anchor === 'start' || anchor === 'end') && textSplit.maxWidth > availableWidth / 2) continue;
            }

            const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + 0.75}em`; // 0.75 is special because rotate..
            data.push({
              text: line,
              color: color2css(dynamicValueStyle.color),
              fontSize: roundTo(dynamicValueStyle.size),
              fontStyle,
              fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
              fontFamily: dynamicValueStyle.fontFamily,
              x: roundTo(x),
              y: roundTo(y),
              dx: 0,
              dy,
              anchor,
            });
          }

          idx++;
        }
      }

      if (valueAxis.placementMode === 'offset') {
        const offsetIdx = axisIdx % chartManager.valueOffsetArray.length;
        const offsetWidth = availableWidth * chartManager.valueOffsetArray.length;

        let offsetHeight = 0;
        for (let idxA = 0; idxA < offsetIdx; idxA++) {
          offsetHeight += chartManager.valueOffsetArray[idxA];
        }

        const textSplit = chartManager.splitTextToWidth(niceObj, offsetWidth, dynamicValueStyle);
        const fontStyle = String(dynamicValueStyle.weight).includes('i') ? 'italic' : undefined;

        const relativeX = chartManager.xScale(obj) + bandWidth / 2;
        const x = chartManager.yAxisWidth + offsetLeft + relativeX;
        const y = chartManager.totalHeight - chartManager.xAxisHeight - marginBottom;

        let idx = 0;
        for (const line of textSplit.lines) {
          let anchor = 'middle';
          if (true) {
            if (relativeX < textSplit.maxWidth / 2) anchor = 'start';
            if (relativeX > chartManager.xScale.range()[1] - textSplit.maxWidth / 2) anchor = 'end';
            // if ((anchor === 'start' || anchor === 'end') && textSplit.maxWidth > availableWidth / 2) continue;
          }

          const dyOffset = offsetHeight * fixLineHeight(dynamicValueStyle.lineheight.value);
          const dy = `${idx * fixLineHeight(dynamicValueStyle.lineheight.value) + dyOffset + 0.75}em`;

          data.push({
            text: line,
            color: color2css(dynamicValueStyle.color),
            fontSize: roundTo(dynamicValueStyle.size),
            fontStyle,
            fontWeight: String(dynamicValueStyle.weight).replace('i', ''),
            fontFamily: dynamicValueStyle.fontFamily,
            x: roundTo(x),
            y: roundTo(y),
            dx: 0,
            dy,
            anchor,
          });

          idx++;
        }

        axisIdx++;
      }
    }

    if (valueAxis.title && valueAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(valueAxis.title, maxWidth, dynamicTitleStyle);
      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = textSplit.lines.length - 1;

      const titleY = chartManager.totalHeight - marginBottom;

      for (const line of textSplit.lines) {
        const dy = `${idx * -fixLineHeight(dynamicTitleStyle.lineheight.value) - 0.25}em`;

        data.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(offsetLeft + chartManager.yAxisWidth + chartManager.chartWidth / 2),
          y: titleY,
          dy,
          anchor: 'middle',
        });

        idx--;
      }
    }

    d3renderText(chartManager.chart.element, data, '.valueAxis');
  }
};
